import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Users, Building2, Search, ChevronUp, ChevronDown, X, TrendingUp, CheckCircle, GraduationCap, ChevronRight } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { organizationsService } from '../services/organizations';
import { enrollmentsService } from '../services/enrollments';
import { supabase } from '../config/supabase';
import { Organization } from '../types';
import { AccountBadge, TrackingBadge } from '../components/LearnerBadges';

const MINT  = '#2f9e5a';
const GOLD  = '#f39200';
const CORAL = '#af3216';

const DEFAULT_DELAY_DAYS = 14;
const DEFAULT_INACTIVE_DAYS = 21;
const getDelayThreshold = () => parseInt(localStorage.getItem('dlm_delay_threshold') || String(DEFAULT_DELAY_DAYS));
const getInactiveThreshold = () => parseInt(localStorage.getItem('dlm_inactive_threshold') || String(DEFAULT_INACTIVE_DAYS));

interface GroupRow {
  id: string;
  name: string;
  description?: string;
  membersCount: number;
  totalEnrollments: number;
  completedEnrollments: number;
  taux: number;
}

type SortColumn = 'name' | 'members' | 'enrollments' | 'taux';
type SortDirection = 'asc' | 'desc';

export const Groups: React.FC = () => {
  const { isSuperAdmin, getUserOrganizationId } = usePermissions();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [viewingGroup, setViewingGroup] = useState<GroupRow | null>(null);

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { if (selectedOrgId) loadGroups(); }, [selectedOrgId]);

  const loadInitialData = async () => {
    try {
      setError(null);
      if (isSuperAdmin) {
        const orgs = await organizationsService.getAll();
        setOrganizations(orgs);
        if (orgs.length > 0) setSelectedOrgId(orgs[0].id);
      } else {
        const orgId = getUserOrganizationId();
        if (orgId) setSelectedOrgId(orgId);
        else setError('Aucune organisation associee a votre compte');
      }
    } catch {
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    if (!selectedOrgId) return;
    try {
      setError(null);

      const [groupsResult, groupLearnersResult, completionRates] = await Promise.all([
        supabase
          .from('groups')
          .select('id, name, description')
          .eq('organization_id', selectedOrgId)
          .order('name'),
        supabase.from('group_learners').select('group_id'),
        enrollmentsService.getGroupCompletionRates(selectedOrgId),
      ]);

      if (groupsResult.error) throw new Error('Impossible de charger les groupes');

      const memberCountMap = new Map<string, number>();
      (groupLearnersResult.data || []).forEach((gl) => {
        const gid = gl.group_id as string;
        memberCountMap.set(gid, (memberCountMap.get(gid) || 0) + 1);
      });

      const rateMap = new Map<string, { totalEnrollments: number; completedEnrollments: number; taux: number }>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      completionRates.forEach((r: any) => {
        rateMap.set(r.groupName, {
          totalEnrollments: r.totalEnrollments,
          completedEnrollments: r.completedEnrollments,
          taux: r.taux,
        });
      });

      const rows: GroupRow[] = (groupsResult.data || []).map((g) => {
        const rate = rateMap.get(g.name);
        return {
          id: g.id,
          name: g.name,
          description: g.description || undefined,
          membersCount: memberCountMap.get(g.id) || 0,
          totalEnrollments: rate?.totalEnrollments || 0,
          completedEnrollments: rate?.completedEnrollments || 0,
          taux: rate?.taux || 0,
        };
      });

      setGroups(rows);
    } catch {
      setError('Erreur lors du chargement des groupes');
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortColumn(column); setSortDirection(column === 'name' ? 'asc' : 'desc'); }
  };

  const filteredGroups = groups
    .filter((g) => !searchQuery || g.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortColumn) {
        case 'name': return dir * a.name.localeCompare(b.name);
        case 'members': return dir * (a.membersCount - b.membersCount);
        case 'enrollments': return dir * (a.totalEnrollments - b.totalEnrollments);
        case 'taux': return dir * (a.taux - b.taux);
        default: return 0;
      }
    });

  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3 text-coral" />
      : <ChevronDown className="w-3 h-3 text-coral" />;
  };

  const progressColor = (pct: number) => pct >= 75 ? MINT : pct >= 50 ? GOLD : CORAL;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink">Groupes</h1>
        <p className="text-ink-soft mt-1">
          {filteredGroups.length} groupe{filteredGroups.length > 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-ink-soft" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="bg-white border border-cream-high rounded-xl text-ink text-sm focus:outline-none focus:ring-2 focus:ring-coral/20 px-3 py-2 w-auto"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-soft" />
          <input
            type="text"
            placeholder="Rechercher un groupe..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30 pl-10 py-2 pr-4"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      {/* Groups Table */}
      <div className="bg-white shadow-ambient rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream-high/30">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  <span className="flex items-center gap-1">Groupe <SortIcon column="name" /></span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer"
                  onClick={() => handleSort('members')}
                >
                  <span className="flex items-center gap-1">Apprenants <SortIcon column="members" /></span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer"
                  onClick={() => handleSort('enrollments')}
                >
                  <span className="flex items-center gap-1">Inscriptions <SortIcon column="enrollments" /></span>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em] cursor-pointer"
                  onClick={() => handleSort('taux')}
                >
                  <span className="flex items-center gap-1">Completion <SortIcon column="taux" /></span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-high">
              {filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-14 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
                      <Users size={32} className="text-gold" />
                    </div>
                    <h3 className="text-lg font-semibold text-ink mb-1">Aucun groupe</h3>
                    <p className="text-sm text-ink-soft">Les groupes sont crees depuis le LMS.</p>
                  </td>
                </tr>
              ) : (
                filteredGroups.map((group) => (
                  <tr key={group.id} className="hover:bg-cream/50 cursor-pointer" onClick={() => setViewingGroup(group)}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-ink">{group.name}</div>
                      {group.description && (
                        <div className="text-sm text-ink-soft truncate max-w-xs">{group.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-ink-soft" />
                        <span className="text-sm text-ink-soft">{group.membersCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-ink-soft">
                        {group.completedEnrollments}/{group.totalEnrollments}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-3 bg-cream-high rounded-full max-w-[120px] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${group.taux}%`, backgroundColor: progressColor(group.taux) }}
                          />
                        </div>
                        <span className="text-sm font-medium text-ink w-10">{group.taux}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingGroup && (
        <GroupDetailModal group={viewingGroup} onClose={() => setViewingGroup(null)} />
      )}
    </div>
  );
};

// ── Types for the detail modal ──
type TrackAlert = 'delayed' | 'inactive' | undefined;

interface CourseLearner {
  learnerId: string;
  fullname: string;
  accountStatus: string;
  progressPercent: number;
  status: string;
  enrolledAt: string | null;
  completedAt: string | null;
}

interface CourseRow {
  courseId: string;
  courseName: string;
  inscrits: number;
  termines: number;
  taux: number;
  learners: CourseLearner[];
}

interface MemberRow {
  learnerId: string;
  fullname: string;
  accountStatus: string;
  overallProgress: number;
  completed: number;
  total: number;
  alertType: TrackAlert;
  daysSince?: number;
}

interface AlertRow {
  learnerId: string;
  fullname: string;
  courseName: string;
  alertType: 'delayed' | 'inactive';
  daysSince: number;
  progressPercent: number;
}

type GroupTab = 'formations' | 'apprenants' | 'alertes';
type ModalSortKey = 'courseName' | 'inscrits' | 'termines' | 'taux';
type ModalSortDir = 'asc' | 'desc';

const GroupDetailModal: React.FC<{ group: GroupRow; onClose: () => void }> = ({ group, onClose }) => {
  const [courseRows, setCourseRows] = useState<CourseRow[]>([]);
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [alertRows, setAlertRows] = useState<AlertRow[]>([]);
  const [activeTab, setActiveTab] = useState<GroupTab>('formations');
  const [loading, setLoading] = useState(true);
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<ModalSortKey>('courseName');
  const [sortDir, setSortDir] = useState<ModalSortDir>('asc');

  useEffect(() => { loadGroupData(); }, [group.id]);

  const loadGroupData = async () => {
    try {
      const { data: members, error: membersErr } = await supabase
        .from('group_learners')
        .select('learner_id, learners!inner(id, fullname, status)')
        .eq('group_id', group.id);

      if (membersErr) throw membersErr;
      if (!members || members.length === 0) {
        setCourseRows([]); setMemberRows([]); setAlertRows([]); setLoading(false); return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const memberMap = new Map<string, { fullname: string; status: string }>();
      for (const m of members as any[]) {
        const learner = Array.isArray(m.learners) ? m.learners[0] : m.learners;
        memberMap.set(m.learner_id, {
          fullname: learner?.fullname || 'Inconnu',
          status: learner?.status || 'active',
        });
      }
      const memberIds = Array.from(memberMap.keys());

      const { data: enrollments, error: enrollErr } = await supabase
        .from('enrollments')
        .select('id, enrolled_at, learner_id, course_id, courses!inner(id, name), course_progress(progress_percent, completed_at, last_activity_at)')
        .in('learner_id', memberIds);

      if (enrollErr) throw enrollErr;

      const nowMs = Date.now();
      const delayMs = getDelayThreshold() * 86400000;
      const inactiveMs = getInactiveThreshold() * 86400000;

      const courseMap = new Map<string, { courseName: string; learners: CourseLearner[] }>();
      // Aggregation per member (for the « Apprenants » tab) — Suivi « vue d'ensemble »
      const agg = new Map<string, { progressSum: number; count: number; completed: number; hasStarted: boolean; hasUnfinished: boolean; lastActivityMs: number; oldestEnrollMs: number }>();
      const alerts: AlertRow[] = [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const e of (enrollments || []) as any[]) {
        const course = Array.isArray(e.courses) ? e.courses[0] : e.courses;
        const courseId = e.course_id as string;
        const courseName = course?.name || 'Inconnu';
        const cp = Array.isArray(e.course_progress) ? e.course_progress[0] : e.course_progress;
        const progressPercent: number = cp?.progress_percent ?? 0;
        const cpCompletedAt: string | null = cp?.completed_at ?? null;
        const member = memberMap.get(e.learner_id);
        const accountStatus = member?.status || 'active';

        let status = 'not_started';
        if (cpCompletedAt || progressPercent >= 100) status = 'completed';
        else if (progressPercent > 0) status = 'in_progress';

        // Suivi alert per enrollment (ignore disabled accounts — no tracking)
        let alertType: TrackAlert;
        let alertDays = 0;
        if (accountStatus !== 'inactive') {
          if (status === 'in_progress' && cp?.last_activity_at) {
            const elapsed = nowMs - new Date(cp.last_activity_at).getTime();
            if (elapsed > delayMs) { alertType = 'delayed'; alertDays = Math.floor(elapsed / 86400000); }
          } else if (status === 'not_started' && e.enrolled_at) {
            const elapsed = nowMs - new Date(e.enrolled_at).getTime();
            if (elapsed > inactiveMs) { alertType = 'inactive'; alertDays = Math.floor(elapsed / 86400000); }
          }
        }

        if (!courseMap.has(courseId)) courseMap.set(courseId, { courseName, learners: [] });
        courseMap.get(courseId)!.learners.push({
          learnerId: e.learner_id,
          fullname: member?.fullname || 'Inconnu',
          accountStatus,
          progressPercent,
          status,
          enrolledAt: e.enrolled_at,
          completedAt: cpCompletedAt,
        });

        // Aggregate for member tab : on agrege l'ensemble des formations de l'apprenant
        const a = agg.get(e.learner_id) || { progressSum: 0, count: 0, completed: 0, hasStarted: false, hasUnfinished: false, lastActivityMs: 0, oldestEnrollMs: Infinity };
        a.progressSum += progressPercent;
        a.count += 1;
        if (status === 'completed') a.completed += 1; else a.hasUnfinished = true;
        if (progressPercent > 0 || status === 'completed') a.hasStarted = true;
        if (cp?.last_activity_at) {
          const t = new Date(cp.last_activity_at).getTime();
          if (t > a.lastActivityMs) a.lastActivityMs = t;
        }
        if (e.enrolled_at) {
          const t = new Date(e.enrolled_at).getTime();
          if (t < a.oldestEnrollMs) a.oldestEnrollMs = t;
        }
        agg.set(e.learner_id, a);

        if (alertType) {
          alerts.push({
            learnerId: e.learner_id,
            fullname: member?.fullname || 'Inconnu',
            courseName,
            alertType,
            daysSince: alertDays,
            progressPercent,
          });
        }
      }

      const rows: CourseRow[] = [];
      courseMap.forEach((val, courseId) => {
        const termines = val.learners.filter(l => l.status === 'completed').length;
        rows.push({
          courseId,
          courseName: val.courseName,
          inscrits: val.learners.length,
          termines,
          taux: val.learners.length > 0 ? Math.round((termines / val.learners.length) * 100) : 0,
          learners: val.learners.sort((a, b) => b.progressPercent - a.progressPercent),
        });
      });

      // All members appear in the « Apprenants » tab, even without enrollment
      const members2: MemberRow[] = [];
      memberMap.forEach((m, learnerId) => {
        const a = agg.get(learnerId);
        let memberAlert: TrackAlert;
        let memberDays: number | undefined;
        if (m.status !== 'inactive' && a) {
          if (!a.hasStarted) {
            // Rien commence du tout → « Pas demarre depuis X j » (plus ancienne inscription)
            if (a.oldestEnrollMs !== Infinity && nowMs - a.oldestEnrollMs > inactiveMs) {
              memberAlert = 'inactive'; memberDays = Math.floor((nowMs - a.oldestEnrollMs) / 86400000);
            }
          } else if (a.hasUnfinished && a.lastActivityMs > 0 && nowMs - a.lastActivityMs > delayMs) {
            // Commence, formations restantes, plus d'activite recente → « En retard depuis X j »
            memberAlert = 'delayed'; memberDays = Math.floor((nowMs - a.lastActivityMs) / 86400000);
          }
        }
        members2.push({
          learnerId,
          fullname: m.fullname,
          accountStatus: m.status,
          overallProgress: a && a.count > 0 ? Math.round(a.progressSum / a.count) : 0,
          completed: a?.completed ?? 0,
          total: a?.count ?? 0,
          alertType: memberAlert,
          daysSince: memberDays,
        });
      });
      members2.sort((a, b) => a.fullname.localeCompare(b.fullname, 'fr'));

      alerts.sort((a, b) => b.daysSince - a.daysSince);

      setCourseRows(rows);
      setMemberRows(members2);
      setAlertRows(alerts);
    } catch (err) {
      console.error('Error loading group detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalMembers = group.membersCount;
  const totalEnrollments = courseRows.reduce((s, c) => s + c.inscrits, 0);
  const totalCompleted = courseRows.reduce((s, c) => s + c.termines, 0);
  const globalRate = totalEnrollments > 0 ? Math.round((totalCompleted / totalEnrollments) * 100) : 0;

  const filteredAndSorted = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const filtered = q
      ? courseRows.filter(c =>
          c.courseName.toLowerCase().includes(q) ||
          c.learners.some(l => l.fullname.toLowerCase().includes(q))
        )
      : courseRows;

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'courseName': cmp = a.courseName.localeCompare(b.courseName, 'fr'); break;
        case 'inscrits': cmp = a.inscrits - b.inscrits; break;
        case 'termines': cmp = a.termines - b.termines; break;
        case 'taux': cmp = a.taux - b.taux; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [courseRows, searchQuery, sortKey, sortDir]);

  const handleSort = (key: ModalSortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'courseName' ? 'asc' : 'desc'); }
  };

  const toggleCourse = (courseId: string) => {
    setExpandedCourses(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '–';

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      not_started: 'bg-cream-high text-ink-soft',
      in_progress: 'bg-lavender-container text-lavender',
      completed:   'bg-mint-container text-mint',
    };
    const labels: Record<string, string> = {
      not_started: 'Non commencé',
      in_progress: 'En cours',
      completed:   'Terminé',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.not_started}`}>
        {labels[status] || status}
      </span>
    );
  };

  const progressBarColor = (pct: number) => pct >= 75 ? MINT : pct >= 50 ? GOLD : CORAL;

  const ModalSortIcon: React.FC<{ columnKey: ModalSortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-coral" />
      : <ChevronDown className="w-3 h-3 text-coral" />;
  };

  const MIN_W = 600;
  const MIN_H = 400;
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalSize, setModalSize] = useState<{ w: number; h: number } | null>(null);
  const dragRef = useRef<{ edge: string; startX: number; startY: number; startW: number; startH: number } | null>(null);

  const onResizeStart = useCallback((edge: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = modalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { edge, startX: e.clientX, startY: e.clientY, startW: rect.width, startH: rect.height };

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      let newW = d.startW;
      let newH = d.startH;
      if (d.edge.includes('e')) newW = d.startW + dx * 2;
      if (d.edge.includes('w')) newW = d.startW - dx * 2;
      if (d.edge.includes('s')) newH = d.startH + dy * 2;
      if (d.edge.includes('n')) newH = d.startH - dy * 2;
      const maxW = window.innerWidth - 32;
      const maxH = window.innerHeight - 32;
      setModalSize({ w: Math.max(MIN_W, Math.min(newW, maxW)), h: Math.max(MIN_H, Math.min(newH, maxH)) });
    };

    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const Handle: React.FC<{ edge: string; className: string }> = ({ edge, className }) => (
    <div className={`absolute z-20 ${className}`} onMouseDown={(e) => onResizeStart(edge, e)} />
  );

  const isFiltered = searchQuery.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-ambient relative"
        style={{
          width: modalSize ? modalSize.w : undefined,
          height: modalSize ? modalSize.h : undefined,
          maxWidth: modalSize ? undefined : '64rem',
          maxHeight: modalSize ? undefined : '90vh',
          ...(modalSize ? {} : { width: '100%' }),
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Handle edge="n"  className="top-0 left-3 right-3 h-1.5 cursor-ns-resize" />
        <Handle edge="s"  className="bottom-0 left-3 right-3 h-1.5 cursor-ns-resize" />
        <Handle edge="w"  className="left-0 top-3 bottom-3 w-1.5 cursor-ew-resize" />
        <Handle edge="e"  className="right-0 top-3 bottom-3 w-1.5 cursor-ew-resize" />
        <Handle edge="nw" className="top-0 left-0 w-3 h-3 cursor-nwse-resize" />
        <Handle edge="ne" className="top-0 right-0 w-3 h-3 cursor-nesw-resize" />
        <Handle edge="sw" className="bottom-0 left-0 w-3 h-3 cursor-nesw-resize" />
        <Handle edge="se" className="bottom-0 right-0 w-3 h-3 cursor-nwse-resize" />

        <div className="overflow-y-auto h-full">
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-cream-high px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-xl font-semibold text-ink truncate">{group.name}</h2>
              {group.description && (
                <span className="text-sm text-ink-soft truncate max-w-xs hidden sm:inline">{group.description}</span>
              )}
            </div>
            <button onClick={onClose} className="text-ink-soft hover:text-coral flex-shrink-0 ml-4 rounded-full p-1 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* KPI Bandeau */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-lavender-container">
                    <Users size={18} className="text-lavender" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : totalMembers}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Membres</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gold-container">
                    <TrendingUp size={18} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : totalEnrollments}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Inscriptions</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-mint-container">
                    <CheckCircle size={18} className="text-mint" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : totalCompleted}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Terminées</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-cream-high rounded-xl p-4 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary-container">
                    <GraduationCap size={18} className="text-coral" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink leading-none">{loading ? '–' : `${globalRate}%`}</p>
                    <p className="text-xs text-ink-soft mt-0.5">Taux de complétion</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-cream-high">
              {([
                ['formations', 'Formations'],
                ['apprenants', 'Apprenants'],
                ['alertes', `Alertes${!loading && alertRows.length > 0 ? ` (${alertRows.length})` : ''}`],
              ] as [GroupTab, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                    activeTab === key
                      ? 'border-coral text-coral'
                      : 'border-transparent text-ink-soft hover:text-ink'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
              </div>
            ) : (
              <>

            {/* ── Onglet APPRENANTS ── */}
            {activeTab === 'apprenants' && (
              memberRows.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-3 text-cream-high" />
                  <p className="text-ink-soft">Aucun membre dans ce groupe</p>
                </div>
              ) : (
                <div className="bg-white border border-cream-high rounded-xl overflow-hidden shadow-ambient">
                  <table className="w-full text-sm">
                    <thead className="bg-cream-high/30">
                      <tr className="text-xs text-ink-soft uppercase tracking-[0.05em]">
                        <th className="text-left px-5 py-2.5">Apprenant</th>
                        <th className="text-left px-5 py-2.5">Compte</th>
                        <th className="text-left px-5 py-2.5">Progression globale</th>
                        <th className="text-left px-5 py-2.5">Formations terminées</th>
                        <th className="text-left px-5 py-2.5">Suivi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-cream-high">
                      {memberRows.map((m) => (
                        <tr key={m.learnerId} className="hover:bg-cream/30">
                          <td className="px-5 py-3 font-semibold text-ink whitespace-nowrap">{m.fullname}</td>
                          <td className="px-5 py-3"><AccountBadge status={m.accountStatus} /></td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[120px] overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${m.overallProgress}%`, backgroundColor: progressBarColor(m.overallProgress) }} />
                              </div>
                              <span className="text-xs text-ink-soft w-8 text-right">{m.overallProgress}%</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm">
                            {m.total === 0
                              ? <span className="text-ink-soft/50">–</span>
                              : <span className="font-medium text-ink">{m.completed}<span className="text-ink-soft font-normal">/{m.total}</span></span>}
                          </td>
                          <td className="px-5 py-3">
                            <TrackingBadge alert={m.alertType} daysSince={m.daysSince} accountStatus={m.accountStatus} compact />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* ── Onglet ALERTES ── */}
            {activeTab === 'alertes' && (
              alertRows.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-mint" />
                  <p className="text-ink-soft">Aucune alerte de suivi pour ce groupe</p>
                </div>
              ) : (
                <div className="bg-white border border-cream-high rounded-xl overflow-hidden shadow-ambient">
                  <table className="w-full text-sm">
                    <thead className="bg-cream-high/30">
                      <tr className="text-xs text-ink-soft uppercase tracking-[0.05em]">
                        <th className="text-left px-5 py-2.5">Apprenant</th>
                        <th className="text-left px-5 py-2.5">Formation</th>
                        <th className="text-left px-5 py-2.5">Suivi</th>
                        <th className="text-left px-5 py-2.5">Progression</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-cream-high">
                      {alertRows.map((a, i) => (
                        <tr key={`${a.learnerId}-${i}`} className="hover:bg-cream/30">
                          <td className="px-5 py-3 font-semibold text-ink whitespace-nowrap">{a.fullname}</td>
                          <td className="px-5 py-3 text-ink-soft">{a.courseName}</td>
                          <td className="px-5 py-3"><TrackingBadge alert={a.alertType} daysSince={a.daysSince} /></td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[100px] overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${a.progressPercent}%`, backgroundColor: progressBarColor(a.progressPercent) }} />
                              </div>
                              <span className="text-xs text-ink-soft w-8 text-right">{a.progressPercent}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* ── Onglet FORMATIONS ── */}
            {activeTab === 'formations' && (
              courseRows.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-cream-high" />
                  <p className="text-ink-soft">Aucune inscription pour ce groupe</p>
                </div>
              ) : (
              <>
                {/* Search bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
                  <input
                    type="text"
                    placeholder="Rechercher par formation ou apprenant..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30 pl-9 pr-4 py-2 text-sm"
                  />
                </div>

                {/* Courses accordion */}
                <div className="bg-white border border-cream-high rounded-xl overflow-hidden shadow-ambient">
                  <div className="px-5 py-3 border-b border-cream-high bg-cream-high/30 flex items-center justify-between">
                    <p className="text-sm font-medium text-ink">
                      {courseRows.length} formation{courseRows.length > 1 ? 's' : ''}
                    </p>
                    {isFiltered && (
                      <p className="text-xs text-ink-soft">
                        {filteredAndSorted.length} resultat{filteredAndSorted.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>

                  {/* Course header row (sortable) */}
                  <div className="grid grid-cols-[2fr_1fr_1fr_1.5fr] px-5 py-2.5 bg-cream-high/30 text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">
                    <button className="flex items-center gap-1 text-left group" onClick={() => handleSort('courseName')}>
                      Formation <ModalSortIcon columnKey="courseName" />
                    </button>
                    <button className="flex items-center gap-1 text-left group" onClick={() => handleSort('inscrits')}>
                      Inscrits <ModalSortIcon columnKey="inscrits" />
                    </button>
                    <button className="flex items-center gap-1 text-left group" onClick={() => handleSort('termines')}>
                      Terminés <ModalSortIcon columnKey="termines" />
                    </button>
                    <button className="flex items-center gap-1 text-left group" onClick={() => handleSort('taux')}>
                      Completion <ModalSortIcon columnKey="taux" />
                    </button>
                  </div>

                  {filteredAndSorted.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-ink-soft">
                      Aucun resultat pour "{searchQuery}"
                    </div>
                  ) : (
                    filteredAndSorted.map((cr) => {
                      const isExpanded = expandedCourses.has(cr.courseId);
                      const q = searchQuery.toLowerCase().trim();
                      const displayLearners = q
                        ? cr.learners.filter(l => l.fullname.toLowerCase().includes(q) || cr.courseName.toLowerCase().includes(q))
                        : cr.learners;

                      return (
                        <div key={cr.courseId} className="border-t border-cream-high/50">
                          <div
                            className="grid grid-cols-[2fr_1fr_1fr_1.5fr] px-5 py-3 hover:bg-cream/30 cursor-pointer items-center"
                            onClick={() => toggleCourse(cr.courseId)}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <ChevronRight
                                className={`w-4 h-4 text-ink-soft flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                              />
                              <span className="text-sm font-semibold text-ink truncate">{cr.courseName}</span>
                            </div>
                            <span className="text-sm text-ink-soft">{cr.inscrits}</span>
                            <span className="text-sm text-ink-soft">{cr.termines}</span>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[100px] overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{ width: `${cr.taux}%`, backgroundColor: progressBarColor(cr.taux) }}
                                />
                              </div>
                              <span className="text-xs text-ink-soft w-8 text-right">{cr.taux}%</span>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="bg-cream-high/20 px-5 pb-3">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-ink-soft uppercase tracking-[0.05em]">
                                    <th className="text-left py-2 pl-6">Apprenant</th>
                                    <th className="text-left py-2">Compte</th>
                                    <th className="text-left py-2">Progression</th>
                                    <th className="text-left py-2">Statut</th>
                                    <th className="text-left py-2">Inscription</th>
                                    <th className="text-left py-2">Completion</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-cream-high">
                                  {displayLearners.map((l) => (
                                    <tr key={l.learnerId} className="hover:bg-white/60">
                                      <td className="py-2 pl-6 font-semibold text-ink whitespace-nowrap">{l.fullname}</td>
                                      <td className="py-2"><AccountBadge status={l.accountStatus} /></td>
                                      <td className="py-2">
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[80px] overflow-hidden">
                                            <div
                                              className="h-full rounded-full transition-all duration-500"
                                              style={{ width: `${l.progressPercent}%`, backgroundColor: progressBarColor(l.progressPercent) }}
                                            />
                                          </div>
                                          <span className="text-xs text-ink-soft w-8 text-right">{l.progressPercent}%</span>
                                        </div>
                                      </td>
                                      <td className="py-2">{statusBadge(l.status)}</td>
                                      <td className="py-2 text-ink-soft whitespace-nowrap">{formatDate(l.enrolledAt)}</td>
                                      <td className="py-2 text-ink-soft whitespace-nowrap">{formatDate(l.completedAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
              )
            )}

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
